import {
  ClearingUpdateStrategy,
  CompartmentSelector,
  EditMode,
  ManualUpdateStrategy,
  UpdaterProfile,
  UpdateStrategy,
  Preset,
  UpdateStrategyType,
  FieldReplacement,
} from '@ekkogmbh/apisdk';
import { Divider, Grid, ListItemText, MenuItem, Stack, Typography } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { StyledTextField } from 'src/Common/Components/Forms/StyledTextField';
import { CoordinateInput } from '../../Common/Components/CoordinateInput';
import { resolveStrategyName } from '../UpdaterProfileManagement';
import { ContentPanel, StepDefinition, usePanel } from 'src/Common/Components/ContentControl/ContentPanel';
import useEkko from 'src/Common/Providers/EkkoProvider';
import useRequest from 'src/Common/Hooks/useRequest';
import useCompartmentGenerator from 'src/CompartmentManagement/Providers/CompartmentGeneratorProvider';
import { CenteredColumn, ReactivePanelColumn } from 'src/Common/Components/Stacks';
import { CompartmentSelectorPicker } from 'src/CompartmentSelectorManagement/CompartmentSelectorPicker';
import { StyledCheckbox } from 'src/Common/Components/Forms/StyledCheckbox';
import { StyledSelectField } from 'src/Common/Components/Forms/StyledSelectField';
import { PresetPicker } from 'src/PresetManagement/PresetPicker';
import { PresetPanelSorting } from 'src/PresetManagement/Components/PresetPanelSorting';
import { CompartmentGeneratorForm } from 'src/CompartmentManagement/Components/CompartmentGeneratorForm';
import usePagination from 'src/Common/Providers/PaginationProvider';
import { enqueueSnackbar } from 'notistack';

const overlineStyle = { fontWeight: 700 };
interface Props {
  profile?: UpdaterProfile;
  closeHandler: () => void;
}
export const UpdaterProfilePanel = ({ profile, closeHandler }: Props): React.JSX.Element => {
  const { api } = useEkko();
  const { refresh } = usePagination();
  const { currentStep, enableNext, enableSave } = usePanel();
  const { fieldKeys, replacements: regexFields, fromReplacements: setRegexFields } = useCompartmentGenerator();
  const [isSaving, startSaving] = useRequest();
  const [isFetchingPreset, startFetchingPreset] = useRequest<Preset>();

  const [preset, setPreset] = useState<Preset>();

  const [name, setName] = useState<string>('');
  const [coordinate, setCoordinate] = useState<string>('');
  const [updateStrategy, setUpdateStrategy] = useState<UpdateStrategy>();
  const [compartmentSelector, setCompartmentSelector] = useState<CompartmentSelector>();
  const [compartmentInputInstructions, setCompartmentInputInstructions] = useState<string | null>();
  const [isMultiSelect, setMultiSelect] = useState<boolean>(false);
  const [isPreserveFields, setPreserveFields] = useState<boolean>(true);

  const reset = useCallback(() => {
    setName(profile?.name ?? '');
    setCoordinate(profile?.coordinate ?? '');
    setUpdateStrategy(profile?.updateStrategy);
    setCompartmentSelector(profile?.compartmentSelector);
    setCompartmentInputInstructions(profile?.compartmentInputInstructions);
    setMultiSelect(profile?.multipleCompartmentSelection ?? false);
    setPreserveFields(profile?.preserveFields ?? true);

    if (profile?.updateStrategy.type === UpdateStrategyType.REGEX_SCAN) {
      setRegexFields((profile.updateStrategy.params.fields ?? []) as FieldReplacement[]);
    }

    // we need to fetch the preset for some strategies
    const presetParam = profile?.updateStrategy.params.preset;
    if (presetParam !== undefined && preset === undefined) {
      startFetchingPreset(
        () => api.getPreset(presetParam as string),
        (p) => setPreset(p),
        () => {
          setPreset(undefined);
          enqueueSnackbar('could not find preset: ' + presetParam, { variant: 'error' });
        },
      );
    }
  }, [api, profile, preset]);

  useEffect(() => {
    reset();
  }, [profile]);

  useEffect(() => {
    switch (currentStep) {
      case 0:
        enableNext(![name, coordinate].includes(''));
        break;
      case 1:
        enableNext(compartmentSelector !== undefined);
        break;
      case 2:
        enableNext(updateStrategy !== undefined);
        break;
      case 3:
        enableSave(true);
    }
  }, [currentStep, name, coordinate, compartmentSelector, updateStrategy]);

  /** set compartment generator fields for specific strategies */
  useEffect(() => {
    if (updateStrategy?.type === UpdateStrategyType.REGEX_SCAN) {
      updateStrategy.params.fields = regexFields;
      setUpdateStrategy({ ...updateStrategy });
    }
  }, [regexFields]);

  /** set preset for strategies that need them */
  useEffect(() => {
    if (
      preset !== undefined &&
      updateStrategy?.type !== undefined &&
      [UpdateStrategyType.CLEARING, UpdateStrategyType.MANUAL].includes(updateStrategy.type)
    ) {
      updateStrategy!.params.preset = preset.name;
      setUpdateStrategy({ ...updateStrategy });
    }
  }, [preset]);

  const saveProfile = useCallback(() => {
    startSaving(
      () =>
        api.saveUpdaterProfile(
          {
            name,
            coordinate,
            updateStrategy: updateStrategy!,
            compartmentSelector: compartmentSelector!,
            multipleCompartmentSelection: isMultiSelect,
            preserveFields: isPreserveFields,
            compartmentInputInstructions,
          },
          profile !== undefined,
        ),
      () => {
        refresh();
        closeHandler();
      },
    );
  }, [
    profile,
    name,
    coordinate,
    isMultiSelect,
    isPreserveFields,
    updateStrategy,
    compartmentSelector,
    compartmentInputInstructions,
  ]);

  const updateStrategyForm = useMemo(() => {
    if (updateStrategy === undefined) {
      return <></>;
    }
    switch (updateStrategy.type) {
      case UpdateStrategyType.MANUAL:
        return (
          <CenteredColumn width={'100%'}>
            <StyledSelectField
              label={'Mode'}
              value={updateStrategy!.params!.editMode ?? EditMode.BULK}
              onChange={(e) => {
                const mode = e.target.value as EditMode;
                setUpdateStrategy({
                  ...updateStrategy,
                  params: {
                    ...updateStrategy.params,
                    editMode: mode,
                  },
                });
              }}
            >
              {Object.values(EditMode).map((mode: EditMode, index: number) => {
                const modeKey = `${index}-${mode}`;
                return (
                  <MenuItem key={modeKey} value={mode}>
                    <ListItemText primary={mode} />
                  </MenuItem>
                );
              })}
            </StyledSelectField>
            <PresetPicker value={preset} onChange={setPreset} />
            {!!preset?.keys && (
              <PresetPanelSorting
                title={'Editable Fields'}
                preset={preset}
                selectedKeys={(updateStrategy as ManualUpdateStrategy).params.disabledFields ?? []}
                onSelect={(keys) => {
                  updateStrategy.params.disabledFields = keys;
                  setUpdateStrategy({ ...updateStrategy });
                }}
              />
            )}
          </CenteredColumn>
        );
      case UpdateStrategyType.CLEARING:
        return (
          <CenteredColumn width={'100%'}>
            <PresetPicker value={preset} onChange={setPreset} />
            <StyledTextField
              type={'text'}
              label={'Clearing Text'}
              tooltip={'All compartment fields that are not disabled will be replaced by this text.'}
              value={updateStrategy?.params.clearingText}
              onChange={(e) => {
                updateStrategy.params.clearingText = String(e.target.value);
                setUpdateStrategy({ ...updateStrategy });
              }}
            />
            {!!preset?.keys && (
              <div>
                <PresetPanelSorting
                  title="Fields to clear"
                  preset={preset}
                  selectedKeys={(updateStrategy as ClearingUpdateStrategy).params.disabledFields ?? []}
                  onSelect={(keys) => {
                    updateStrategy.params.disabledFields = keys;
                    setUpdateStrategy({ ...updateStrategy });
                  }}
                />
              </div>
            )}
          </CenteredColumn>
        );
      case UpdateStrategyType.REGEX_SCAN:
        return (
          <CenteredColumn width={'100%'}>
            <StyledTextField
              type={'text'}
              label={'Regex Text'}
              tooltip={'All compartment fields that are not disabled will be replaced by this text'}
              value={updateStrategy?.params.regexText ?? ''}
              onChange={(e) => {
                updateStrategy.params.regexText = String(e.target.value);
                setUpdateStrategy({ ...updateStrategy });
              }}
            />
            <CompartmentGeneratorForm />
          </CenteredColumn>
        );
      case undefined:
        return <></>;
    }
  }, [updateStrategy, preset]);

  const idStep = useMemo(
    (): StepDefinition => ({
      title: 'Updater Profile',
      description: <Typography color={'primary'}>{name + (coordinate ? '@' + coordinate : '')}</Typography>,
      contentCallback: () => (
        <ReactivePanelColumn>
          <StyledTextField
            disabled={profile !== undefined}
            type="text"
            label="Name"
            value={name}
            onChange={(e) => {
              setName(String(e.target.value));
            }}
          />
          <CoordinateInput
            disabled={profile !== undefined}
            value={coordinate}
            onChange={setCoordinate}
            trailingDelimiter={false}
          />
        </ReactivePanelColumn>
      ),
    }),
    [profile, name, coordinate],
  );

  const csStep = useMemo(
    () => ({
      title: 'Compartment Selection',
      contentCallback: () => (
        <ReactivePanelColumn>
          <CompartmentSelectorPicker
            coordinate={coordinate}
            selected={compartmentSelector}
            onChange={setCompartmentSelector}
          />
          <StyledTextField
            type={'text'}
            label={'Compartment Input Instructions'}
            tooltip={'Instruction text displayed when applying the profile.'}
            value={compartmentInputInstructions ?? ''}
            onChange={(e) => setCompartmentInputInstructions(String(e.target.value))}
          />
          <StyledCheckbox
            label={'Select from multiple Compartments'}
            value={isMultiSelect}
            onChange={() => setMultiSelect(!isMultiSelect)}
            tooltip={'Display a selection in the Shopfloor App when the input matches multiple compartments.'}
          />
        </ReactivePanelColumn>
      ),
    }),
    [compartmentSelector, compartmentInputInstructions, isMultiSelect, coordinate],
  );

  const configStep = useMemo(
    () => ({
      title: 'Configuration',
      contentCallback: () => (
        <ReactivePanelColumn lgWidth={'66%'}>
          <Stack direction={'row'} justifyContent={'space-between'}>
            <StyledSelectField
              label={'Type'}
              value={updateStrategy?.type ?? ''}
              onChange={(e) => {
                const uType = e.target.value as UpdateStrategyType;
                const newStrat = { type: uType, params: {} };
                switch (uType) {
                  case UpdateStrategyType.CLEARING:
                    newStrat.params = { clearingText: '' };
                    break;
                  case UpdateStrategyType.REGEX_SCAN:
                    newStrat.params = { regexText: '' };
                }
                setUpdateStrategy(newStrat);
              }}
            >
              {Object.values(UpdateStrategyType).map((type: UpdateStrategyType, index: number) => (
                <MenuItem key={index} value={type}>
                  <ListItemText primary={resolveStrategyName(type)} />
                </MenuItem>
              ))}
            </StyledSelectField>
            <StyledCheckbox
              label={'Preserve Fields'}
              tooltip={
                'When unchecked, fields that are not part of the configuration will be removed when applying an update'
              }
              value={isPreserveFields}
              onChange={() => setPreserveFields(!isPreserveFields)}
            />
          </Stack>
          {updateStrategy !== undefined && <Divider />}
          {updateStrategyForm}
        </ReactivePanelColumn>
      ),
    }),
    [isPreserveFields, updateStrategyForm, updateStrategy],
  );

  const overviewStep = useMemo(
    () => ({
      title: 'Overview',
      contentCallback: () => (
        <>
          {updateStrategy && compartmentSelector && (
            <Grid container spacing={2} alignItems="stretch" style={{ wordBreak: 'break-word' }}>
              <Grid item xs={6}>
                <Typography variant="overline" color="primary" style={overlineStyle}>
                  Name
                </Typography>
                <Typography variant="h6" gutterBottom>
                  {name}
                </Typography>
                <Typography variant="overline" color="primary" style={overlineStyle}>
                  Coordinate
                </Typography>
                <Typography variant="h6" gutterBottom>
                  {coordinate}
                </Typography>
                <Typography variant="overline" color="primary" style={overlineStyle}>
                  Compartment Selector
                </Typography>
                <Typography variant="h6" gutterBottom>
                  {`${compartmentSelector?.name ?? ''}@${
                    compartmentSelector?.coordinate ?? ''
                  } [${compartmentSelector?.type ?? ''}]`}
                </Typography>
                <Typography variant="overline" color="primary" style={overlineStyle}>
                  Multiple Compartment Selection
                </Typography>
                <Typography variant="h6" gutterBottom>
                  {`${isMultiSelect ? 'Yes' : 'No'}`}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="overline" color="primary" style={overlineStyle}>
                  Type
                </Typography>
                <Typography variant="h6" gutterBottom>
                  {resolveStrategyName((updateStrategy as UpdateStrategy).type)}
                </Typography>
                {(updateStrategy as UpdateStrategy).type === 'manual' && (
                  <>
                    <Typography variant="overline" color="primary" style={overlineStyle}>
                      Preset
                    </Typography>
                    <Typography variant="h6" gutterBottom>
                      {(updateStrategy as ManualUpdateStrategy).params.preset}
                    </Typography>
                    <Typography variant="overline" color="primary" style={overlineStyle}>
                      Mode
                    </Typography>
                    <Typography variant="h6" gutterBottom>
                      {(updateStrategy as ManualUpdateStrategy).params.editMode ?? EditMode.BULK}
                    </Typography>
                    {(updateStrategy as ManualUpdateStrategy).params.disabledFields &&
                      (updateStrategy as ManualUpdateStrategy).params.disabledFields.length > 0 && (
                        <>
                          <Typography variant="overline" color="primary" style={overlineStyle}>
                            Disabled Fields
                          </Typography>
                          <Typography variant="h6" gutterBottom>
                            {(updateStrategy as ManualUpdateStrategy).params.disabledFields.join(', ')}
                          </Typography>
                        </>
                      )}
                  </>
                )}
                {(updateStrategy as UpdateStrategy).type === 'regex-scan' && (
                  <>
                    <Typography variant="overline" color="primary" style={overlineStyle}>
                      Regex Fields
                    </Typography>
                    <Typography variant="h6" gutterBottom>
                      {fieldKeys.join(', ')}
                    </Typography>
                  </>
                )}
                {(updateStrategy as UpdateStrategy).type === 'clearing' && (
                  <>
                    <Typography variant="overline" color="primary" style={overlineStyle}>
                      Preset
                    </Typography>
                    <Typography variant="h6" gutterBottom>
                      {(updateStrategy as ClearingUpdateStrategy).params.preset}
                    </Typography>
                    {(updateStrategy as ClearingUpdateStrategy).params.disabledFields?.length > 0 && (
                      <>
                        <Typography variant="overline" color="primary" style={overlineStyle}>
                          Disabled Fields
                        </Typography>
                        <Typography variant="h6" gutterBottom>
                          {(updateStrategy as ClearingUpdateStrategy).params.disabledFields?.join(', ')}
                        </Typography>
                      </>
                    )}
                    {updateStrategy?.params.clearingText && (
                      <>
                        <Typography variant="overline" color="primary" style={overlineStyle}>
                          Clearing Text
                        </Typography>
                        <Typography variant="h6" gutterBottom>
                          {updateStrategy.params.clearingText}
                        </Typography>
                      </>
                    )}
                  </>
                )}
                <Typography variant="overline" color="primary" style={overlineStyle}>
                  Preserve Fields
                </Typography>
                <Typography variant="h6" gutterBottom>
                  {isPreserveFields ? 'Yes' : 'No'}
                </Typography>
              </Grid>
            </Grid>
          )}
        </>
      ),
    }),
    [isPreserveFields, updateStrategy, name, coordinate, isMultiSelect, fieldKeys, compartmentSelector],
  );

  return (
    <ContentPanel
      isLoading={isSaving || isFetchingPreset}
      initialStep={profile === undefined ? 0 : 1}
      steps={[idStep, csStep, configStep, overviewStep]}
      onCancel={closeHandler}
      onReset={reset}
      onSave={saveProfile}
    />
  );
};
